"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Smile } from "lucide-react";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";

interface EmojiPickerButtonProps {
  onSelect: (emoji: string) => void;
  /** Custom trigger element. Receives onClick to toggle the picker. */
  children?: (props: {
    onClick: () => void;
    isOpen: boolean;
  }) => React.ReactNode;
  /** Tailwind classes for the default trigger button. */
  className?: string;
}

/**
 * Self-contained emoji picker: a trigger button that opens the
 * @emoji-mart/react Picker above it. Handles open/close state and
 * outside-click dismissal internally.
 */
export default function EmojiPickerButton({
  onSelect,
  children,
  className = "rounded p-1.5 text-muted-foreground transition-colors hover:text-foreground",
}: EmojiPickerButtonProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const toggle = useCallback(() => setOpen((prev) => !prev), []);

  const handleEmojiSelect = useCallback(
    (emoji: { native: string }) => {
      onSelect(emoji.native);
      setOpen(false);
    },
    [onSelect],
  );

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (
        pickerRef.current &&
        !pickerRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative">
      {children ? (
        children({ onClick: toggle, isOpen: open })
      ) : (
        <button
          ref={triggerRef}
          type="button"
          onClick={toggle}
          aria-label="Add emoji"
          className={className}
        >
          <Smile className="h-4 w-4" />
        </button>
      )}
      {open && (
        <div
          ref={pickerRef}
          className="absolute bottom-full left-0 z-50 mb-2"
        >
          <Picker
            data={data}
            onEmojiSelect={handleEmojiSelect}
            theme="light"
            previewPosition="none"
            skinTonePosition="search"
          />
        </div>
      )}
    </div>
  );
}
